'use client'

import {
  Pagination as PaginationComponent,
  PaginationContent,
  PaginationItem,
} from '@/components/ui/pagination'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import { buildAuthorProfilePagePath } from '@/utilities/authorProfilePaths'
import { buildPostsPagePath } from '@/utilities/getPostUrlPrefix'
import { useTaxonomyArchivePaths } from '@/providers/TaxonomyArchivePathsContext'
import {
  buildCategoryArchivePagePath,
  buildTagArchivePagePath,
} from '@/utilities/taxonomyArchivePaths'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { LocaleLink } from '@/components/LocaleLink'
import { useTranslations } from 'next-intl'
import React from 'react'

/** Modo serializável (RSC) para construir URLs de paginação — não passar funções ao cliente. */
export type PaginationLinkMode =
  | { kind: 'posts'; urlPrefix?: string }
  | { kind: 'tag'; slug: string }
  | { kind: 'category'; slug: string }
  | { kind: 'author'; userId: number }

type Props = {
  className?: string
  page: number
  totalPages: number
  /** Prefixo do arquivo de posts quando `linkMode` é ausente ou `kind: 'posts'`. */
  urlPrefix?: string
  linkMode?: PaginationLinkMode
}

function hrefForPageNumber(
  n: number,
  linkMode: PaginationLinkMode | undefined,
  urlPrefix: string | undefined,
  showCategoryBaseInSlug: boolean,
  categoryUrlBase: string,
  tagUrlBase: string,
): string {
  if (linkMode) {
    switch (linkMode.kind) {
      case 'posts':
        return buildPostsPagePath(n, linkMode.urlPrefix ?? urlPrefix)
      case 'tag':
        return buildTagArchivePagePath(linkMode.slug, n, tagUrlBase)
      case 'category':
        return buildCategoryArchivePagePath(
          linkMode.slug,
          n,
          showCategoryBaseInSlug,
          categoryUrlBase,
        )
      case 'author':
        return buildAuthorProfilePagePath(linkMode.userId, n)
    }
  }
  return buildPostsPagePath(n, urlPrefix)
}

const pageNumClass = (isActive: boolean) =>
  cn(
    buttonVariants({
      size: 'icon',
      variant: isActive ? 'outline' : 'ghost',
    }),
    'inline-flex h-9 w-9 items-center justify-center',
  )

const navBtnClass = cn(
  buttonVariants({ size: 'default', variant: 'ghost' }),
  'inline-flex items-center gap-1',
)

export const Pagination: React.FC<Props> = ({
  className,
  page,
  totalPages,
  urlPrefix,
  linkMode,
}) => {
  const t = useTranslations('Pagination')
  const { showCategoryBaseInSlug, categoryUrlBase, tagUrlBase } = useTaxonomyArchivePaths()
  const hasNextPage = page < totalPages
  const hasPrevPage = page > 1
  const hasExtraPrevPages = page - 1 > 1
  const hasExtraNextPages = page + 1 < totalPages

  const href = (n: number) =>
    hrefForPageNumber(n, linkMode, urlPrefix, showCategoryBaseInSlug, categoryUrlBase, tagUrlBase)

  return (
    <div className={cn('mt-8 mb-0 sm:mt-10', className)}>
      <PaginationComponent>
        <PaginationContent>
          <PaginationItem>
            {hasPrevPage ? (
              <LocaleLink
                href={href(page - 1)}
                className={cn(navBtnClass, 'pl-2.5')}
                aria-label={t('prevAria')}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                <span>{t('prev')}</span>
              </LocaleLink>
            ) : (
              <span
                className={cn(navBtnClass, 'cursor-not-allowed pl-2.5 opacity-50')}
                aria-disabled="true"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                <span>{t('prev')}</span>
              </span>
            )}
          </PaginationItem>

          {hasExtraPrevPages && (
            <PaginationItem>
              <span aria-hidden className="flex h-9 w-9 items-center justify-center">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('morePages')}</span>
              </span>
            </PaginationItem>
          )}

          {hasPrevPage && (
            <PaginationItem>
              <LocaleLink href={href(page - 1)} className={pageNumClass(false)}>
                {page - 1}
              </LocaleLink>
            </PaginationItem>
          )}

          <PaginationItem>
            <LocaleLink href={href(page)} className={pageNumClass(true)} aria-current="page">
              {page}
            </LocaleLink>
          </PaginationItem>

          {hasNextPage && (
            <PaginationItem>
              <LocaleLink href={href(page + 1)} className={pageNumClass(false)}>
                {page + 1}
              </LocaleLink>
            </PaginationItem>
          )}

          {hasExtraNextPages && (
            <PaginationItem>
              <span aria-hidden className="flex h-9 w-9 items-center justify-center">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('morePages')}</span>
              </span>
            </PaginationItem>
          )}

          <PaginationItem>
            {hasNextPage ? (
              <LocaleLink
                href={href(page + 1)}
                className={cn(navBtnClass, 'pr-2.5')}
                aria-label={t('nextAria')}
              >
                <span>{t('next')}</span>
                <ChevronRight className="h-4 w-4" aria-hidden />
              </LocaleLink>
            ) : (
              <span
                className={cn(navBtnClass, 'cursor-not-allowed pr-2.5 opacity-50')}
                aria-disabled="true"
              >
                <span>{t('next')}</span>
                <ChevronRight className="h-4 w-4" aria-hidden />
              </span>
            )}
          </PaginationItem>
        </PaginationContent>
      </PaginationComponent>
    </div>
  )
}
